// components/InternalAudit.jsx
import { useContext, useEffect, useState } from "react"
import { AppContext } from "../App"
import { getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import HorizontalImagesStrip from "../ones/HorizontalImagesStrip";

import banner1 from '../assets/banner1.jpg';
import banner2 from '../assets/banner2.jpg';
import banner3 from '../assets/banner3.jpg';
import banner4 from '../assets/banner4.jpg';

import {
  IoMdCheckmarkCircleOutline,
  IoMdSearch,
  IoMdBuild,
  IoMdArrowDropright,
  IoMdPeople,
  IoMdDocument,
  IoMdAlert,
  IoMdTrendingUp,
  IoMdLock
} from "react-icons/io";
import { FaBalanceScale, FaShieldAlt, FaChartNetwork, FaPaperclip } from "react-icons/fa";
import { FaShield } from "react-icons/fa6";

export default function InternalAudit(props) {

  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  
  // Banner rotation for hero section
  const banners = [banner1, banner2, banner3, banner4, banner1, banner3];

  // Internal Audit Service Pillars - ATRIC Profile Aligned
  const servicePillars = [
    {
      id: 1,
      title: "Internal Controls Design & Review",
      icon: <FaShieldAlt />,
      description: "Comprehensive assessment and enhancement of internal control frameworks to safeguard assets, ensure data integrity, and support reliable financial reporting.",
      features: [
        "Control Environment Assessment & Documentation",
        "Process Walkthroughs, Testing & Gap Analysis",
        "Control Remediation Plans & Implementation Support"
      ],
      gradient: "from-amber-500/20 to-orange-500/10",
      accent: "#f59e0b"
    },
    {
      id: 2,
      title: "Risk-Based Audit & Investigations",
      icon: <IoMdSearch />,
      description: "Independent, risk-focused audit engagements and forensic investigations to identify vulnerabilities, detect irregularities, and strengthen organizational resilience.",
      features: [
        "Risk Assessment & Audit Planning (Annual & Ad-Hoc)",
        "Fraud Investigation, Data Analytics & Evidence Collection",
        "Whistleblower Support & Disciplinary Process Advisory"
      ],
      gradient: "from-red-500/20 to-rose-500/10",
      accent: "#ef4444"
    },
    {
      id: 3,
      title: "Governance & Compliance Assurance",
      icon: <FaBalanceScale />,
      description: "Evaluation of governance structures, policy adherence, and regulatory compliance to promote accountability, transparency, and ethical business conduct.",
      features: [
        "Board & Committee Effectiveness Reviews",
        "Policy Compliance Audits (HR, Finance, Operations)",
        "Regulatory Compliance Assessments & Reporting"
      ],
      gradient: "from-blue-500/20 to-indigo-500/10",
      accent: "#60a5fa"
    },
    {
      id: 4,
      title: "Operational Efficiency & Process Improvement",
      icon: <IoMdTrendingUp />,
      description: "Process-focused audits that identify inefficiencies, recommend optimizations, and support management in achieving operational excellence and cost savings.",
      features: [
        "End-to-End Process Mapping & Bottleneck Identification",
        "Performance Benchmarking & Best Practice Recommendations",
        "Change Management Support for Process Implementation"
      ],
      gradient: "from-emerald-500/20 to-teal-500/10",
      accent: "#10b981"
    }
  ];

  // Audit Engagement Methodology
  const methodologySteps = [
    {
      step: "01",
      title: "Plan & Scope",
      desc: "Risk-based audit planning, stakeholder alignment, and clear definition of objectives, scope, and success criteria",
      icon: <IoMdDocument size={24} />,
      deliverables: ["Audit Charter", "Risk Assessment Matrix", "Audit Program"]
    },
    {
      step: "02",
      title: "Execute & Test",
      desc: "Fieldwork execution including interviews, document review, control testing, and data analytics to gather evidence",
      icon: <IoMdSearch size={24} />,
      deliverables: ["Working Papers", "Test Results", "Preliminary Findings"]
    },
    {
      step: "03",
      title: "Report & Recommend",
      desc: "Clear, actionable reporting with root cause analysis, risk ratings, and practical recommendations for improvement",
      icon: <IoMdDocument size={24} />,
      deliverables: ["Draft Report", "Management Response", "Final Audit Report"]
    },
    {
      step: "04",
      title: "Follow-up & Sustain",
      desc: "Monitoring implementation of recommendations, validating remediation, and embedding continuous improvement",
      icon: <IoMdCheckmarkCircleOutline size={24} />,
      deliverables: ["Action Plan Tracker", "Remediation Validation", "Lessons Learned"]
    }
  ];

  // Audit Focus Areas / Industries
  const focusAreas = [
    { name: "Financial Controls", icon: "💰" },
    { name: "Operational Processes", icon: "⚙️" },
    { name: "IT & Cyber Security", icon: "🔐" },
    { name: "Procurement & Supply Chain", icon: "📦" },
    { name: "HR & Payroll", icon: "👥" },
    { name: "Compliance & Regulatory", icon: "⚖️" },
    { name: "Project & Grant Management", icon: "🎯" },
    { name: "Fraud & Forensic", icon: "🔍" }
  ];

  // ATRIC Internal Audit Differentiators
  const differentiators = [
    {
      title: "Independent & Objective",
      desc: "Unbiased assurance with direct reporting lines to Audit Committees and Boards",
      icon: <FaShield size={20} />
    },
    {
      title: "Risk-Focused Approach",
      desc: "Prioritization based on impact, likelihood, and strategic relevance to your organization",
      icon: <IoMdAlert size={20} />
    },
    {
      title: "Practical Recommendations",
      desc: "Actionable, cost-effective solutions aligned with your operational reality and resources",
      icon: <IoMdBuild size={20} />
    },
    {
      title: "Capacity Building",
      desc: "Knowledge transfer and staff development to strengthen your internal audit function long-term",
      icon: <IoMdPeople size={20} />
    }
  ];

  async function init() {
    setReady(false);
    setTimeout(() => setReady(true), 300);
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!ready) return;
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [ready]);

  if (!ready) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      {/* ================= HERO SECTION ================= */}
      <section className="MainBodySection position-relative">
        <div style={{
          width: "100%",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Rotating Banner Backgrounds */}
          {banners.map((banner, i) => (
            <div
              key={i}
              style={{
                backgroundImage: `url(${banner})`,
                backgroundAttachment: "fixed",
                backgroundSize: "cover",
                backgroundPosition: "center",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                transition: "opacity 2s ease-in-out",
                opacity: i === bgIndex ? 1 : 0,
                zIndex: 0
              }}
              aria-hidden={i !== bgIndex}
            />
          ))}

          {/* Dark Overlay with Content */}
          <div style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 50%, rgba(15,23,42,0.7) 100%)",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            backdropFilter: "blur(2px)"
          }}>
            <div className="container py-5">
              <div className="row align-items-center">
                <div className="col-lg-8">
                  <span className="d-inline-block px-3 py-1 mb-3 rounded-pill" 
                    style={{ 
                      background: "rgba(245,158,11,0.15)", 
                      border: "1px solid rgba(245,158,11,0.3)", 
                      color: "#f59e0b", 
                      fontSize: "13px", 
                      fontWeight: "500" 
                    }}>
                    Internal Audit Excellence
                  </span>
                  
                  <h1 className="text-light mb-3" style={{
                    fontSize: "clamp(2rem, 4vw, 3.2rem)",
                    fontWeight: "700",
                    lineHeight: "1.15",
                    letterSpacing: "-0.5px"
                  }}>
                    Strengthen Controls. Mitigate Risk. Build Resilience.
                  </h1>

                  <p className="text-light mt-3 mb-4" style={{
                    fontSize: "1.1rem",
                    opacity: 0.9,
                    lineHeight: "1.7",
                    maxWidth: "650px"
                  }}>
                    ATRIC provides independent, risk-based internal audit services that assess governance, controls, and risk management processes—delivering actionable insights to protect value and support sustainable growth.
                  </p>

                  <div className="d-flex flex-wrap gap-3">
                    <button
                      className="btn btn-warning btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                      onClick={() => appContext.navTo({ item: 'contact_us', service: 'Internal Audit' })}
                      style={{ 
                        background: "linear-gradient(135deg, #d97706, #f59e0b)",
                        border: "none",
                        fontWeight: "500",
                        boxShadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
                        color: "#1e293b"
                      }}
                    >
                      Request Audit Consultation
                      <IoMdArrowDropright size={20} />
                    </button>
                    <a 
                      href="#methodology" 
                      className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill"
                      style={{ 
                        borderColor: "rgba(255,255,255,0.3)",
                        fontWeight: "500"
                      }}
                    >
                      View Our Approach
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VALUE PROPOSITION ================= */}
      <section style={{
        background: "linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)",
        padding: "70px 0"
      }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9">
              <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2rem" }}>
                Why Internal Audit Matters
              </h2>
              <p className="text-light opacity-75 mb-4" style={{ lineHeight: "1.7", fontSize: "1.05rem" }}>
                Internal Audit is a critical component of good governance and risk management. It provides independent assurance that an organization's risk management, governance, and internal control processes are operating effectively—helping leadership make informed decisions and preserve stakeholder value.
              </p>
              
              {/* Core Principles Badges */}
              <div className="d-flex flex-wrap justify-content-center gap-2 mb-4">
                {[
                  { name: "Independence", icon: <FaShield size={14} /> },
                  { name: "Objectivity", icon: <FaBalanceScale size={14} /> },
                  { name: "Professional Skepticism", icon: <IoMdSearch size={14} /> },
                  { name: "Value Addition", icon: <IoMdTrendingUp size={14} /> }
                ].map((item, idx) => (
                  <div key={idx} className="badge rounded-pill px-3 py-2 d-flex align-items-center gap-2" 
                    style={{ 
                      background: "rgba(255,255,255,0.08)", 
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "#e2e8f0",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}>
                    {item.icon}
                    <span>{item.name}</span>
                  </div>
                ))}
              </div>

              <HorizontalImagesStrip
                images={[banner1, banner2, banner3, banner4]}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICE PILLARS GRID ================= */}
      <section style={{
        background: "#0f172a",
        padding: "80px 0"
      }} id="services">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="text-light mb-3" style={{ fontWeight: "600", fontSize: "2.2rem" }}>
              Comprehensive Internal Audit Services
            </h2>
            <p className="text-light opacity-75" style={{ maxWidth: "700px", margin: "0 auto", lineHeight: "1.6" }}>
              Four integrated pillars delivering independent assurance, risk insight, and practical improvements to strengthen your organization's governance and control environment.
            </p>
          </div>

          <div className="row g-4">
            {servicePillars.map((pillar) => (
              <div className="col-md-6 col-lg-3" key={pillar.id}>
                <div 
                  className="h-100 p-4 rounded-3 border position-relative overflow-hidden"
                  style={{
                    background: `linear-gradient(145deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))`,
                    backdropFilter: "blur(12px)",
                    borderColor: "rgba(255,255,255,0.08)",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = "0 20px 40px -15px rgba(0, 0, 0, 0.4)";
                    e.currentTarget.style.borderColor = pillar.accent + "66";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Learn more about ${pillar.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }
                  }}
                >
                  {/* Decorative gradient accent */}
                  <div className="position-absolute top-0 end-0 p-3 opacity-20" 
                    style={{ 
                      background: `linear-gradient(135deg, ${pillar.gradient.split(' ')[0].replace('/20','/40')}, ${pillar.gradient.split(' ')[1].replace('/10','/25')})`,
                      width: "80px",
                      height: "80px",
                      borderRadius: "0 0 0 100%",
                      zIndex: 0
                    }} 
                  />

                  {/* Icon Badge */}
                  <div className="d-inline-flex align-items-center justify-content-center rounded-2 p-2 mb-3"
                    style={{
                      background: `linear-gradient(135deg, ${pillar.gradient})`,
                      border: `1px solid rgba(255,255,255,0.15)`,
                      color: "#fff",
                      width: "48px",
                      height: "48px",
                      fontSize: "1.3rem",
                      zIndex: 1
                    }}>
                    {pillar.icon}
                  </div>

                  {/* Service Content */}
                  <h5 className="text-light mb-2" style={{ fontWeight: "600", fontSize: "1.25rem", zIndex: 1 }}>
                    {pillar.title}
                  </h5>
                  <p className="text-light opacity-80 mb-3 small" style={{ lineHeight: "1.6", zIndex: 1 }}>
                    {pillar.description}
                  </p>

                  {/* Feature List */}
                  <ul className="list-unstyled mb-4" style={{ zIndex: 1 }}>
                    {pillar.features.map((feature, idx) => (
                      <li key={idx} className="d-flex align-items-start gap-2 mb-2 small">
                        <IoMdCheckmarkCircleOutline 
                          className="flex-shrink-0 mt-0.5" 
                          size={16} 
                          style={{ color: pillar.accent }} 
                        />
                        <span className="text-light opacity-85" style={{ fontSize: "0.9rem" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Link */}
                  <a 
                    href="#contact"
                    className="d-inline-flex align-items-center gap-1 text-decoration-none"
                    style={{ 
                      color: pillar.accent, 
                      fontWeight: "500", 
                      fontSize: "0.9rem",
                      zIndex: 1,
                      transition: "gap 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.gap = "6px"}
                    onMouseLeave={(e) => e.currentTarget.style.gap = "4px"}
                    onClick={(e) => {
                      e.preventDefault();
                      appContext.navTo({ item: 'contact_us', service: pillar.title });
                    }}
                  >
                    Learn More
                    <IoMdArrowDropright size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= DIFFERENTIATORS ================= */}
      <section style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "60px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="text-center mb-4">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              The ATRIC Internal Audit Advantage
            </h3>
          </div>
          <div className="row g-4 justify-content-center">
            {differentiators.map((item, idx) => (
              <div className="col-md-6 col-lg-3" key={idx}>
                <div 
                  className="p-4 rounded-3 h-100"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.transform = "translateY(-3px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: "44px",
                      height: "44px",
                      background: "linear-gradient(135deg, #f59e0b, #fb923c)",
                      color: "#1e293b",
                      fontWeight: "600"
                    }}>
                    {item.icon}
                  </div>
                  <h6 className="text-light mb-2" style={{ fontWeight: "600" }}>{item.title}</h6>
                  <p className="text-light opacity-75 small mb-0" style={{ lineHeight: "1.6" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FOCUS AREAS ================= */}
      <section style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        padding: "60px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="text-center mb-4">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Audit Focus Areas & Industries
            </h3>
          </div>
          <div className="row g-3 justify-content-center">
            {focusAreas.map((area, idx) => (
              <div className="col-6 col-sm-4 col-md-3" key={idx}>
                <div 
                  className="p-3 rounded-2 text-center"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div className="mb-2" style={{ fontSize: "1.5rem" }}>{area.icon}</div>
                  <span className="text-light small" style={{ fontWeight: "500", fontSize: "0.85rem" }}>
                    {area.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= METHODOLOGY ================= */}
      <section id="methodology" style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding: "75px 0"
      }}>
        <div className="container">
          <div className="text-center mb-5">
            <h3 className="text-light mb-2" style={{ fontWeight: "600" }}>
              Our Internal Audit Methodology
            </h3>
            <p className="text-light opacity-75">A structured, risk-based approach aligned with International Professional Practices Framework (IPPF)</p>
          </div>

          <div className="row g-4 justify-content-center">
            {methodologySteps.map((step, idx) => (
              <div className="col-md-6 col-lg-3" key={idx}>
                <div className="text-center p-4 h-100" style={{ position: "relative" }}>
                  {/* Connector line for desktop */}
                  {idx < 3 && (
                    <div className="d-none d-lg-block position-absolute top-50 end-0" 
                      style={{ 
                        width: "100%", 
                        height: "1px", 
                        background: "linear-gradient(to right, rgba(245,158,11,0.3), transparent)",
                        transform: "translateY(-50%) translateX(50%)",
                        zIndex: 0
                      }} 
                    />
                  )}
                  
                  <div className="position-relative z-1">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 mx-auto"
                      style={{
                        width: "56px",
                        height: "56px",
                        background: "linear-gradient(135deg, #d97706, #f59e0b)",
                        color: "#1e293b",
                        fontWeight: "700",
                        fontSize: "1.1rem",
                        boxShadow: "0 8px 20px -5px rgba(245, 158, 11, 0.4)"
                      }}>
                      {step.icon}
                    </div>
                    <div className="badge bg-warning bg-opacity-10 text-warning mb-2" style={{ fontSize: "0.8rem", fontWeight: "600", color: "#f59e0b" }}>
                      Phase {step.step}
                    </div>
                    <h6 className="text-light mb-2" style={{ fontWeight: "600" }}>{step.title}</h6>
                    <p className="text-light opacity-75 small mb-2" style={{ lineHeight: "1.6" }}>{step.desc}</p>
                    
                    {/* Key Deliverables */}
                    <div className="mt-3 pt-2 border-top" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                      <small className="text-light opacity-60 d-block mb-1">Key Deliverables:</small>
                      {step.deliverables.map((del, i) => (
                        <span key={i} className="badge me-1 mb-1" style={{ 
                          background: "rgba(255,255,255,0.1)", 
                          fontSize: "0.75rem",
                          fontWeight: "400",
                          color: "#e2e8f0"
                        }}>
                          {del}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INTEGRATION HIGHLIGHT ================= */}
      <section style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        padding: "60px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)"
      }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <div className="p-4 rounded-3" style={{
                background: "rgba(245,158,11,0.1)",
                border: "1px solid rgba(245,158,11,0.3)"
              }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <IoMdLock size={32} style={{ color: "#f59e0b" }} />
                  <h4 className="text-light mb-0" style={{ fontWeight: "600" }}>Integrated Assurance Approach</h4>
                </div>
                <p className="text-light opacity-85 mb-3" style={{ lineHeight: "1.6" }}>
                  ATRIC's Internal Audit services integrate seamlessly with our broader advisory offerings to provide holistic risk and control insights:
                </p>
                <ul className="list-unstyled mb-0">
                  {[
                    "Coordination with External Audit & Tax Teams",
                    "Alignment with Management Accounting & Performance Frameworks",
                    "Support for Regulatory Compliance & Investment Facilitation",
                    "Cyber Risk Assessment Integrated with IT Controls Review",
                    "Capacity Building for Sustainable Internal Audit Functions"
                  ].map((item, idx) => (
                    <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                      <IoMdCheckmarkCircleOutline size={18} style={{ color: "#10b981", flexShrink: 0 }} />
                      <span className="text-light opacity-90 small">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="text-center text-lg-start">
                <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                  Build Confidence Through Independent Assurance
                </h3>
                <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                  Whether you need a full internal audit function outsourced, a targeted review of specific processes, or support strengthening your existing team, ATRIC delivers objective, practical, and value-adding assurance services tailored to your organizational context.
                </p>
                <a 
                  href="#contact"
                  className="d-inline-flex align-items-center gap-2 text-decoration-none"
                  style={{ color: "#f59e0b", fontWeight: "500" }}
                  onClick={(e) => {
                    e.preventDefault();
                    appContext.navTo({ item: 'contact_us', service: 'Internal Audit' });
                  }}
                >
                  Discuss Your Assurance Needs
                  <IoMdArrowDropright size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section id="contact" style={{
        background: "linear-gradient(135deg, #0c1424 0%, #1e293b 100%)",
        padding: "65px 0",
        borderTop: "1px solid rgba(255,255,255,0.08)"
      }}>
        <div className="container text-center">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <FaPaperclip size={42} className="mb-3" style={{ color: "#f59e0b" }} />
              <h3 className="text-light mb-3" style={{ fontWeight: "600" }}>
                Ready to Strengthen Your Control Environment?
              </h3>
              <p className="text-light opacity-80 mb-4" style={{ lineHeight: "1.7" }}>
                Partner with ATRIC's experienced audit professionals for independent assurance, practical insights, and sustainable improvements. Let's build organizational resilience together.
              </p>
              
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <button
                  className="btn btn-warning btn-lg px-5 py-2 rounded-pill"
                  onClick={() => appContext.navTo({ item: 'contact_us', service: 'Internal Audit' })}
                  style={{ 
                    background: "linear-gradient(135deg, #d97706, #f59e0b)",
                    border: "none",
                    fontWeight: "600",
                    boxShadow: "0 6px 20px rgba(245, 158, 11, 0.3)",
                    color: "#1e293b"
                  }}
                >
                  Schedule Audit Consultation
                </button>
                <a 
                  href="tel:+255717918808"
                  className="btn btn-outline-light btn-lg px-4 py-2 rounded-pill d-flex align-items-center gap-2"
                  style={{ borderColor: "rgba(255,255,255,0.25)" }}
                >
                  <span style={{ fontSize: "1.1rem" }}>📞</span>
                  +255 717 918 808
                </a>
              </div>
              
              <p className="text-light opacity-60 small mt-4 mb-0">
                📍 2nd Floor, The Tax Hub Building, Industrial Road, Mikocheni B, Dar es Salaam
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}